// resources/js/components/EvaluationList.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Button,
  Collapse,
  useMediaQuery,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

const ConferenceEvaluationList = () => {
  const eventid = localStorage.getItem('eventid');
  const { eventId } = useParams();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customFields, setCustomFields] = useState([]);
  const [openRow, setOpenRow] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch evaluations
        const evalResponse = await axios.get(`/api/evaluations/event/${eventid}`);
        setEvaluations(evalResponse.data.data);

        // Fetch custom fields
        const fieldsResponse = await axios.get(
          `/api/load-customfield-evaluations/12/${eventid}`
        );
        setCustomFields(fieldsResponse.data.customFieldLoad || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventid]);

  // Organize custom fields into sections
  const organizeCustomFields = (fields) => {
    const grouped = {};

    fields.forEach((field) => {
      const order = field.order_by || 999;

      if (!grouped[order]) {
        grouped[order] = {
          order,
          title: null,
          questions: []
        };
      }

      if (!field.type) {
        // Section header
        grouped[order].title = field.name;
      } else {
        // Question under section
        grouped[order].questions.push(field);
      }
    });

    return Object.values(grouped).sort((a, b) => a.order - b.order);
  };

  // Render response values
  const renderResponseValue = (response, field) => {
    if (!response) return '-';

    if (field?.type === 'file') {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <InsertDriveFileIcon color="action" />
          <a href={response.value} target="_blank" rel="noopener noreferrer">
            View File
          </a>
        </Box>
      );
    }

    try {
      const parsed = JSON.parse(response.value);
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }
    } catch {
      // Not JSON
    }

    return <Typography>{String(response.value)}</Typography>;
  };

  // Calculate comprehensive response statistics for ALL response types
  const calculateResponseStats = (evaluation) => {
    const responseCounts = {};
    let totalResponses = 0;
    const allResponses = [];

    // Count all response types
    Object.values(evaluation.responses).forEach(response => {
      const value = String(response?.value || '').trim();
      
      if (!value || value === 'null' || value === 'undefined') {
        return;
      }

      allResponses.push(value);
      totalResponses++;

      if (responseCounts[value]) {
        responseCounts[value]++;
      } else {
        responseCounts[value] = 1;
      }
    });

    // Convert to array for charts
    const responseData = Object.entries(responseCounts)
      .map(([name, value]) => ({ 
        name, 
        value,
        percentage: totalResponses > 0 ? Math.round((value / totalResponses) * 100) : 0
      }))
      .sort((a, b) => b.value - a.value); // Sort by count descending

    // Calculate Yes/No/Other specifically
    let yesCount = 0;
    let noCount = 0;
    let otherCount = 0;

    Object.values(evaluation.responses).forEach(response => {
      const value = String(response?.value || '').toLowerCase().trim();
      if (value === 'yes') {
        yesCount++;
      } else if (value === 'no') {
        noCount++;
      } else if (value && value !== 'null' && value !== 'undefined') {
        otherCount++;
      }
    });

    const yesNoData = [
      { name: 'Yes', value: yesCount },
      { name: 'No', value: noCount },
      { name: 'Other', value: otherCount }
    ];

    return {
      responseData,
      yesNoData,
      total: totalResponses,
      responseCounts,
      yesCount,
      noCount,
      otherCount,
      yesPercentage: totalResponses > 0 ? Math.round((yesCount / totalResponses) * 100) : 0,
      noPercentage: totalResponses > 0 ? Math.round((noCount / totalResponses) * 100) : 0,
      otherPercentage: totalResponses > 0 ? Math.round((otherCount / totalResponses) * 100) : 0
    };
  };

  // Colors for charts - dynamic based on response types
  const generateColors = (count) => {
    const baseColors = [
      '#4caf50', '#2196f3', '#ff9800', '#f44336', '#9c27b0',
      '#00bcd4', '#8bc34a', '#ffeb3b', '#795548', '#607d8b',
      '#e91e63', '#3f51b5', '#009688', '#ff5722', '#673ab7'
    ];
    
    return baseColors.slice(0, count);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, mt: 13, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: 7 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Evaluation Submissions
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 3 }}>
        Total Participants Evaluated: <strong>{evaluations.length}</strong>
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const csv = convertEvaluationsToCSV(evaluations);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'evaluation_responses.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        >
          Download CSV
        </Button>

        <Button
          variant="contained"
          color="primary"
          component={Link}
          to={{
            pathname: '/admin/conference/chart-report',
            state: { evaluations, customFields }
          }}
        >
          Open Chart Report
        </Button>
      </Box>

      {evaluations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">No evaluations found for this event.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Participant</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Submitted On</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Responses</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {evaluations.map((evaluation) => {
                const sections = organizeCustomFields(customFields);
                const stats = calculateResponseStats(evaluation);
                const colors = generateColors(stats.responseData.length);

                return (
                  <React.Fragment key={evaluation.id}>
                    <TableRow hover>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <Typography fontWeight="bold">
                            {evaluation.participant?.participant_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {evaluation.participant?.participant_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {new Date(evaluation.created_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() =>
                            setOpenRow(openRow === evaluation.id ? null : evaluation.id)
                          }
                        >
                          {openRow === evaluation.id ? 'Hide' : 'Show'} Responses
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ p: 0 }}>
                        <Collapse
                          in={openRow === evaluation.id}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ p: 2 }}>
                            {/* Summary Statistics */}
                            <Grid container spacing={3} sx={{ mb: 3 }}>
                              {/* All Responses Pie Chart */}
                              <Grid item xs={12} md={6}>
                                <Card>
                                  <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                      All Responses Distribution
                                    </Typography>
                                    {stats.responseData.length > 0 ? (
                                      <Box sx={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                          <PieChart>
                                            <Pie
                                              data={stats.responseData}
                                              cx="50%"
                                              cy="50%"
                                              labelLine={false}
                                              label={({ name, percentage }) => 
                                                `${name}: ${percentage}%`
                                              }
                                              outerRadius={80}
                                              fill="#8884d8"
                                              dataKey="value"
                                            >
                                              {stats.responseData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                              ))}
                                            </Pie>
                                            <Tooltip 
                                              formatter={(value, name) => [`${value} (${Math.round((value/stats.total)*100)}%)`, name]}
                                            />
                                            <Legend />
                                          </PieChart>
                                        </ResponsiveContainer>
                                      </Box>
                                    ) : (
                                      <Typography sx={{ textAlign: 'center', py: 4 }}>
                                        No response data available
                                      </Typography>
                                    )}
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* Response Details */}
                              <Grid item xs={12} md={6}>
                                <Card>
                                  <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                      Response Summary
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      {/* Yes/No/Other Summary */}
                                      <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                          Yes/No Breakdown:
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <Typography variant="body2">Yes Responses:</Typography>
                                          <Typography variant="body2" fontWeight="bold" color="success.main">
                                            {stats.yesCount} ({stats.yesPercentage}%)
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <Typography variant="body2">No Responses:</Typography>
                                          <Typography variant="body2" fontWeight="bold" color="error.main">
                                            {stats.noCount} ({stats.noPercentage}%)
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <Typography variant="body2">Other Responses:</Typography>
                                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                            {stats.otherCount} ({stats.otherPercentage}%)
                                          </Typography>
                                        </Box>
                                      </Box>

                                      {/* All Response Types */}
                                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        All Response Types:
                                      </Typography>
                                      {stats.responseData.map((response, index) => (
                                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <Typography variant="body2">
                                            {response.name}:
                                          </Typography>
                                          <Typography variant="body2" fontWeight="bold">
                                            {response.value} ({response.percentage}%)
                                          </Typography>
                                        </Box>
                                      ))}
                                      
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                                        <Typography variant="subtitle1">Total Questions:</Typography>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                          {stats.total}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>

                            {/* Detailed Responses */}
                            {sections.map((group, groupIndex) => (
                              <Box key={groupIndex} sx={{ mb: 3 }}>
                                {/* Section Header */}
                                {group.title && (
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      color: 'primary.main',
                                      fontWeight: 'bold',
                                      mb: 1,
                                      borderBottom: '2px solid',
                                      borderColor: 'primary.light',
                                      pb: 0.5
                                    }}
                                  >
                                    {group.title}
                                  </Typography>
                                )}

                                {/* Questions */}
                                {group.questions.map((field, qIndex) => (
                                  <Box
                                    key={qIndex}
                                    sx={{
                                      mb: 1.5,
                                      p: 1.5,
                                      backgroundColor: '#f9f9f9',
                                      borderRadius: 2,
                                      borderLeft: '3px solid',
                                      borderColor: 'primary.light'
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      color="text.primary"
                                      fontWeight="medium"
                                      gutterBottom
                                    >
                                      {field.name}
                                    </Typography>
                                    <Box sx={{ pl: 1 }}>
                                      {renderResponseValue(
                                        evaluation.responses[field.name],
                                        field
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

// Convert evaluations to CSV
const convertEvaluationsToCSV = (evaluations) => {
  const rows = [];

  evaluations.forEach((evalData) => {
    const { participant, responses } = evalData;

    const row = {
      Name: participant?.participant_name || '',
      Email: participant?.participant_email || '',
      SubmittedAt: new Date(evalData.created_at).toLocaleString()
    };

    Object.entries(responses).forEach(([question, response]) => {
      row[question] = response?.value ?? '';
    });

    rows.push(row);
  });

  const headers = Object.keys(rows[0] || {});
  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((h) => `"${(row[h] ?? '').toString().replace(/"/g, '""')}"`)
        .join(',')
    )
  ].join('\n');

  return csv;
};

export default ConferenceEvaluationList;